<template>
  <RexModalV3
    :show="showModal"
    @onClose="handleClose"
    :title="$t('apdexTitle')"
    :enableFooter="true"
    width="50rem"
  >
    <template #body>
      <div>
        <DataRetentionTabs
          :items="tabItems"
          :selectedItem="currentTab.name"
          @onClick="onTabClick"
        />

        <div class="apdex-app-list-tab-container">
          <RexSearch
            v-if="currentTab.serviceType === tabItems[0].serviceType"
            width="100%"
            :placeholder="$t('searchApplication')"
            @search="handleSearch"
            @clearSelection="clearSearch"
            class="apdex-app-list-search"
          />
          <RexSearch
            v-else
            width="100%"
            :placeholder="$t('searchApplication')"
            @search="handleSearch"
            @clearSelection="clearSearch"
            class="apdex-app-list-search"
          />

          <div class="apdex-app-list-status">
            <span>{{ $t('healthStatus') }}</span>
            <RexSearchSelect
              width="10rem"
              :current="currentStatus"
              :data="healthStatuses"
              @onChoose="selectStatus"
              :objectValueIndex="1"
              id="apdex-app-list-status"
              :isSearch="false"
            />
          </div>
        </div>

        <RexTableCompV2
          :headers="headers"
          :items="appListData.records"
          :loading="loading"
          variant="secondary"
          :itemHeight="63.5"
          @fetchNextPageData="
            () => {
              if (allowInfiniteScroll) {
                currentPage++;
                fetchAppList();
              }
            }
          "
          :enableScrollFooter="allowInfiniteScroll"
          :enableInfiniteScroll="true"
          height="30rem"
        >
          <template
            v-for="header in headers"
            #[`header-${header.key}`]
            :key="header.key"
          >
            <div>
              {{ header.name }}
            </div>
          </template>

          <template #body-respTime="{ item }">
            <div class="apdex-app-list-respTime">
              {{ item.respTime }}
              <div @click="() => redirectToAPM(item)">
                <BaseIcon
                  name="redirectTimeSM"
                  class="apdex-app-list-redirect-icon"
                  width="1.2rem"
                />
              </div>
            </div>
          </template>
          <template #body-apdex="{ item }">
            <div :class="`apdex-app-list-${currentStatus.key.toLowerCase()}`">
              {{ item.apdex }}
            </div>
          </template>
        </RexTableCompV2>
      </div>
    </template>

    <template #footer>
      <span></span>
    </template>
  </RexModalV3>
</template>

<script setup lang="ts">
import DataRetentionTabs from '@/components/Settings/DataRetentionTabs.vue';
import RexModalV3 from '@/rex-components/RexModalV3.vue';
import {
  APDEX_APPLIST_TABS,
  HEALTH_STATUSES,
  APDEX_APPLIST_HEADERS,
  DEFAULT_APP_LIST,
  DEFAULT_PAGE,
} from '@/components/Home/revamp/util.js';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import RexSearch from '@/rex-components/rex-search.vue';
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import { ref, computed, defineProps, onMounted, watch } from 'vue';
import { useActions, useGetters } from '@/utils/useMappers';
import {
  Props,
  AppListDataType,
  Tab,
  SearchParam,
  Status,
  ListRecord,
} from '@/components/Home/revamp/home.types';
import {
  parseApdex,
  parseServiceObjFromApplication,
} from '@/utils/helper-function';
import { useRouter } from 'vue-router';

const [getApdexAppList, selectBrowserService] = useActions([
  'homeState/getApdexAppList',
  'browserState/browserCommonState/selectBrowserService',
]);
const { showModal, onClose, apdexApplistStatus } = defineProps<Props>();

const router = useRouter();

const currentTab = ref(APDEX_APPLIST_TABS()[0]);
const loading = ref(false);
const searchText = ref('');
const healthStatuses = computed(() => HEALTH_STATUSES());
const currentStatus = ref(apdexApplistStatus || HEALTH_STATUSES()[0]);
const headers = computed(() => APDEX_APPLIST_HEADERS());
const appListData = ref<AppListDataType>(DEFAULT_APP_LIST);
const currentPage = ref(DEFAULT_PAGE);

const [getTimeDuration, teamId] = useGetters([
  'globalState/settings/getDurationTime',
  'globalState/user/getTeamId',
]);

const tabItems = computed(function () {
  return APDEX_APPLIST_TABS();
});

const allowInfiniteScroll = computed(function () {
  return appListData.value.records.length < appListData.value.total;
});

const handleClose = () => {
  onClose();
};
const clearSearch = () => {
  searchText.value = '';
  handleSearch({
    text: '',
  });
};

const onTabClick = (data: Tab) => {
  currentTab.value = data;
  searchText.value = '';
};
const selectStatus = (status: Status) => {
  currentStatus.value = status;
};
const handleSearch = (searchParam: SearchParam) => {
  searchText.value = searchParam.text;
  currentPage.value = DEFAULT_PAGE;
  fetchAppList(true);
};
const fetchAppList = async (reset = false) => {
  const params = {
    condition: {
      keyword: searchText.value,
      filterCondition: {
        scope: 'Service',
        metricType: 'ALERTS',
        order: 'DES',
        apdexHealth: currentStatus.value.key,
      },
      pagination: {
        pageNum: currentPage.value,
        pageSize: 10,
        needTotal: true,
      },
      duration: getTimeDuration.value,
      serviceReq: {
        capability: currentTab.value.serviceType,
        teamID: teamId.value,
        checkCorrelation: true,
        correlatedCapability: 'V',
      },
    },
  };
  try {
    if (reset === true) {
      appListData.value = DEFAULT_APP_LIST;
      loading.value = true;
    }
    const res: AppListDataType = await getApdexAppList(params);
    const dataWithUpdatedApdex = res.records.map((record) => ({
      ...record,
      apdex: parseApdex(record.apdex),
    }));
    appListData.value = {
      total: res.total,
      records: [
        ...(!reset ? appListData.value.records : []),
        ...dataWithUpdatedApdex,
      ],
    };
  } catch (error) {
    console.log(error);
  } finally {
    loading.value = false;
  }
};

const redirectToAPM = (item: ListRecord) => {
  if (currentTab.value.serviceType.toLowerCase() === 'browser') {
    router.push({
      name: currentTab.value.serviceType.toLowerCase(),
      query: {
        id: item.serviceID,
      },
    });
  } else {
    router.push({
      name: currentTab.value.serviceType.toLowerCase(),
      query: {
        id: item.serviceID,
      },
    });
  }
};

onMounted(() => {
  fetchAppList();
});

watch(currentStatus, () => {
  currentPage.value = DEFAULT_PAGE;
  fetchAppList(true);
});

watch(currentTab, () => {
  currentPage.value = DEFAULT_PAGE;
  fetchAppList(true);
});

watch(apdexApplistStatus, (newValue) => {
  currentStatus.value = newValue;
});
</script>

<style lang="scss" scoped>
.apdex-app-list-tab-container {
  @include flex-center-justify(space-between);
  gap: 2rem;
}

:deep(.dataRetentionTab) {
  position: inherit;
  :deep(.item) {
    width: 100%;
  }
}

:deep(.dataRetentionTab .item) {
  width: 100%;
}

.apdex-app-list-status {
  @include flex-center-vert;
  gap: 1rem;
}

.apdex-app-list-search {
  margin: 1rem 0;
  flex: 1;
}

.apdex-app-list-respTime {
  width: 100%;
  @include flex-center-justify(space-between);
  gap: 0.5rem;
}

.apdex-app-list-redirect-icon {
  margin-right: 1rem;
  cursor: pointer;

  :deep(.cls-1) {
    fill: $color-brand-accent;
  }
}

.apdex-app-list-good {
  color: $good;
}

.apdex-app-list-fair {
  color: $fair;
}

.apdex-app-list-poor {
  color: $poor;
}
</style>
